<template>
    <div class="summary">
        <p>Summary</p>
    </div>
    <div class="type">
        <span>License ({{ LicenseTypeName[selectedPlanType] }})</span>
        <p v-if="store.isLoadingLicenseCosts" class="loading-price">
            <LoadingDots />
        </p>
        <p v-else>
            {{ formatCurrency(totalCostBeforeDiscounts) }}
        </p>
    </div>
    <div class="type">
        <span>Annual Discount</span>
        <p v-if="store.isLoadingLicenseCosts" class="loading-price">
            <LoadingDots />
        </p>
        <p v-else>-{{ formatCurrency(totalAnnualDiscount) }}</p>
    </div>
    <div class="type" v-if="totalMultiLicenseDiscount > 0">
        <span>Multi License Discount</span>
        <p v-if="store.isLoadingLicenseCosts" class="loading-price">
            <LoadingDots />
        </p>
        <p v-else>-{{ formatCurrency(totalMultiLicenseDiscount) }}</p>
    </div>
    <div v-if="giftCertAppliedAmount > 0" class="type">
        <span>Gift Card ({{ giftCertAppliedCode }})</span>
        <p>-{{ formatCurrency(giftCertAppliedAmount * 100) }}</p>
    </div>
    <div v-if="store.creditBalance > 0" class="type">
        <span>Credit Balance ({{ formatCurrency(store.creditBalance * 100) }})</span>
        <p>-{{ formatCurrency(store.creditAmountUsed) }}</p>
    </div>
    <div v-if="purchaseType === PurchaseType.UPGRADE && !license?.refundable" class="type">
        <span>License Credit <span class="annotation">(1)</span></span>
        <p>-{{ formatCurrency(license?.prorate_amount) }}</p>
    </div>
    <div v-if="purchaseType === PurchaseType.UPGRADE && license?.refundable" class="type">
        <span>License Refund <span class="annotation">(2)</span></span>
        <p>-{{ formatCurrency(license?.prorate_amount) }}</p>
    </div>
    <div class="due">
        <span v-if="totalCostAfterDiscounts < 0 && license?.refundable">Refund Amount</span>
        <span v-else-if="totalCostAfterDiscounts < 0 && !license?.refundable">
            Credit Received <span class="annotation">(3)</span>
        </span>
        <span v-else>Due Today</span>
        <p v-if="store.isLoadingLicenseCosts" class="loading-price">
            <LoadingDots />
        </p>
        <p v-else>
            {{ formatCurrency(totalCostAfterDiscounts) }}
        </p>
    </div>
    <div v-if="purchaseType === PurchaseType.UPGRADE" class="text-zinc-400 font-inter text-xs font-normal leading-4 mt-4">
        <div>(1) The license credit is estimated and based on any multi-license discounts you may have.  This may impact the Due Today.</div>
        <div v-if="license?.refundable">(2) If your previous license was more expensive, you will be refunded the difference after the credit is applied directly to your original payment method.</div>
        <div v-if="totalCostAfterDiscounts < 0 && !license?.refundable">(3) You will receive a credit to be used on future renewals or new licenses because the license is outside the refund period.</div>
    </div>
</template>
<script setup lang="ts">
import { useSubscribeStore } from '@/stores/components/modals/subscription-modal';
import LoadingDots from '~/components/main/shared/LoadingDots.vue';
import useUtils from '~/composables/useUtils';
import { LicenseTypeName, PurchaseType } from '~/types/licenses/licenses';

const store = useSubscribeStore();
const { formatCurrency } = useUtils();

const {
    selectedPlanType,
    totalCostBeforeDiscounts,
    totalAnnualDiscount,
    totalMultiLicenseDiscount,
    totalCostAfterDiscounts,
    giftCertAppliedAmount,
    giftCertAppliedCode,
    license,
    purchaseType,
    creditAmountUsed
} = storeToRefs(store);
</script>
<style lang="scss" scoped>
.summary {
    @apply flex items-center gap-1 self-stretch pb-3;

    p {
        @apply text-xl font-semibold leading-8 text-zinc-900 font-inter;
    }
}

.type {
    @apply flex justify-between items-center self-stretch py-3 border-b border-dashed border-zinc-300;

    span {
        @apply text-zinc-800 font-inter text-base font-normal leading-8;
    }

    p {
        @apply text-zinc-950 text-right font-inter text-base font-medium leading-8;
    }
}

.due {
    @apply flex justify-between items-center self-stretch py-3 border-dashed border-b border-zinc-300;

    span {
        @apply text-zinc-800 font-inter text-base font-semibold leading-8;
    }

    p {
        @apply text-green-700 text-right font-inter text-base font-medium leading-8;
    }
}
.divider-div {
    @apply relative w-full flex items-center pt-8 pb-5;
    .divider {
        @apply border border-zinc-300 absolute inset-x-0;
    }
}

.due .annotation, .type .annotation {
    @apply text-zinc-400 text-xs;
}
</style>
